<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon"><i class="fa fa-sticky-note"></i></div>
        <div class="panel-header__text">
          <div class="panel-header__title">Notes</div>
          <div class="panel-header__sub">Manage system notes and disclosure messages shown to applicants</div>
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="dataLoading">
        <div class="state-spinner"></div>
        <span>Loading notes…</span>
      </div>

      <!-- Editors -->
      <div class="editors-wrap" v-else>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-times-circle"></i></div>
            <div>
              <div class="editor-section__title">Invalid Company Abbreviation Code</div>
              <div class="editor-section__sub">Shown when an invalid abbreviation code is entered</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="invalid_abbr" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-clock"></i></div>
            <div>
              <div class="editor-section__title">Expired Company Abbreviation Code</div>
              <div class="editor-section__sub">Shown when an abbreviation code has expired</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="expired_abbr" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-barcode"></i></div>
            <div>
              <div class="editor-section__title">Company Abbreviation Code Note</div>
              <div class="editor-section__sub">General note displayed on the abbreviation code entry screen</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="abbreviation_code_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-calendar-times"></i></div>
            <div>
              <div class="editor-section__title">No Schedule Note</div>
              <div class="editor-section__sub">Shown when no available schedule slots are found</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="no_schedule_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-id-card"></i></div>
            <div>
              <div class="editor-section__title">Personal Identity Note</div>
              <div class="editor-section__sub">Displayed during the personal identity verification step</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="personal_identity_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-check-circle"></i></div>
            <div>
              <div class="editor-section__title">Appointment Confirm Detail Note</div>
              <div class="editor-section__sub">Shown on the appointment confirmation detail screen</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="appointment_confirm_detail_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-truck"></i></div>
            <div>
              <div class="editor-section__title">Appointment Shipping Note</div>
              <div class="editor-section__sub">Displayed when shipping details are collected</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="appointment_shipping_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-shield-alt"></i></div>
            <div>
              <div class="editor-section__title">FBI Check Disclosure Note</div>
              <div class="editor-section__sub">Disclosure shown before FBI background check submission</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="fbi_check_disclosure_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-university"></i></div>
            <div>
              <div class="editor-section__title">FINRA Disclosure Note</div>
              <div class="editor-section__sub">Regulatory disclosure for FINRA-related submissions</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="finra_disclosure_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-certificate"></i></div>
            <div>
              <div class="editor-section__title">Apostille Disclosure Note</div>
              <div class="editor-section__sub">Disclosure presented for apostille document requests</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="no-api-key" v-model="apostille_disclosure_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

        <div class="editor-section editor-section--last">
          <div class="editor-section__header">
            <div class="editor-section__icon"><i class="fa fa-search"></i></div>
            <div>
              <div class="editor-section__title">Biometric Data Collection Disclosure</div>
              <div class="editor-section__sub">Consent disclosure for biometric data collection</div>
            </div>
          </div>
          <div class="editor-section__body">
            <editor api-key="71vg7medn8k1guoqbic2prdchbq0em4dqvhruo5ko352ck6h"
              v-model="biometric_data_collection_disclosure_note" :init="config"
              tinymce-script-src="/tinymce/js/tinymce/tinymce.min.js" />
          </div>
        </div>

      </div>
    </div>

    <!-- Floating save bar -->
    <div class="save-bar" v-if="!dataLoading">
      <span class="save-bar__hint">
        <i class="fa fa-info-circle"></i> Changes are saved when you click Update
      </span>
      <button class="save-bar__update" ref="kt_update_note" @click="updateNote()">
        <i class="fa fa-save"></i> Update
      </button>
    </div>
  </div>
</template>
<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { FETCH_NOTES, UPDATE_NOTES } from "@/core/services/store/actions.type";
import Editor from "@tinymce/tinymce-vue";

export default {
  components: { editor: Editor },
  name: "app",
  data() {
    return {
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Setting", route: "" },
        { id: 3, title: "Notes", route: "" },
      ],

      booknow_note: "",
      abbreviation_code_note: "",
      no_schedule_note: "",
      personal_identity_note: "",
      appointment_confirm_detail_note: "",
      appointment_shipping_note: "",
      finra_disclosure_note: "",
      apostille_disclosure_note: "",
      biometric_data_collection_disclosure_note: "",
      fbi_check_disclosure_note: "",
      expired_abbr: "",
      invalid_abbr: "",
      dataLoading: true,

      id: 0,
      config: {
        license_key: 'gpl',
        height: 500,
        menubar: false,
        plugins: [
          "advlist autolink lists link image charmap print preview anchor",
          "searchreplace visualblocks code fullscreen",
          "insertdatetime media table paste code help wordcount"
        ],
        toolbar:
          "undo redo | formatselect | fontsizeselect |bold italic backcolor | \
           alignleft aligncenter alignright alignjustify | \
           bullist numlist outdent indent | removeformat | code | help",
        fontsize_formats:
          "8pt 9pt 10pt 11pt 12pt 14pt 18pt 24pt 30pt 36pt 48pt 60pt 72pt 96pt"
      }
    };
  },
  created() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
    this.getNote();
  },

  methods: {
    getNote() {
      this.dataLoading = true;
      this.$store
        .dispatch(FETCH_NOTES)
        .then(res => {
          var data = res.data;
          this.id = data.id ? data.id : 0;
          this.abbreviation_code_note = data.abbreviation_code_note;
          this.no_schedule_note = data.no_schedule_note;
          this.personal_identity_note = data.personal_identity_note;
          this.appointment_confirm_detail_note =
            data.appointment_confirm_detail_note;
          this.appointment_shipping_note = data.appointment_shipping_note;
          this.fbi_check_disclosure_note = data.fbi_check_disclosure_note;
          this.finra_disclosure_note = data.finra_disclosure_note;
          this.apostille_disclosure_note = data.apostille_disclosure_note;
          this.biometric_data_collection_disclosure_note = data.biometric_data_collection_disclosure_note;
          this.expired_abbr = data.expired_abbr;
          this.invalid_abbr = data.invalid_abbr;
          this.dataLoading = false;
        })
        .catch(() => {
          this.dataLoading = false;
        });
    },
    updateNote() {
      const submitButton = this.$refs["kt_update_note"];
      submitButton.disabled = true;
      submitButton.classList.add("spinner", "spinner-light", "spinner-right");
      var data = {
        form_data: {
          abbreviation_code_note: this.abbreviation_code_note,
          no_schedule_note: this.no_schedule_note,
          personal_identity_note: this.personal_identity_note,
          appointment_confirm_detail_note: this.appointment_confirm_detail_note,
          appointment_shipping_note: this.appointment_shipping_note,
          fbi_check_disclosure_note: this.fbi_check_disclosure_note,
          finra_disclosure_note: this.finra_disclosure_note,
          apostille_disclosure_note: this.apostille_disclosure_note,
          biometric_data_collection_disclosure_note: this.biometric_data_collection_disclosure_note,
          expired_abbr: this.expired_abbr,
          invalid_abbr: this.invalid_abbr
        },
        id: this.id
      };
      this.$store.dispatch(UPDATE_NOTES, data).then(res => {
        this.id = res.i_id;
        this.$toastr.s(res.msg);
        submitButton.classList.remove(
          "spinner",
          "spinner-light",
          "spinner-right"
        );
        submitButton.disabled = false;
      });
    }
  }
};
</script>

<style lang="scss" scoped>
$font: 'Segoe UI', sans-serif;
$blue: #389ad4;
$dark: #0d1b3e;
$border: #dde2ec;

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.page-wrap {
  padding: 12px 12px 96px;
  font-family: $font;
}

.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text {
    flex: 1;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 3px;
  }
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 56px 24px;
  gap: 8px;
  color: #7a8499;
  font-size: 13px;
  font-family: $font;
}

.state-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #c5d8f5;
  border-top-color: $blue;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin-bottom: 8px;
}

.editors-wrap {
  padding: 0;
}

.editor-section {
  border-bottom: 1px solid $border;
  padding: 24px 28px;

  &--last {
    border-bottom: none;
  }

  &__header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
  }

  &__icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: rgba(56, 154, 212, 0.1);
    color: $blue;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 1px;
  }

  &__title {
    font-size: 14px;
    font-weight: 700;
    color: $dark;
    line-height: 1.3;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 2px;
  }

  &__body {
    border: 1.5px solid $border;
    border-radius: 10px;
    overflow: hidden;
  }
}

.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(13, 27, 62, 0.3);
  z-index: 100;

  &__hint {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.55);
    font-family: $font;

    i {
      font-size: 12px;
    }
  }

  &__update {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: $blue;
    color: #fff;
    border: none;
    border-radius: 9px;
    padding: 9px 22px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover {
      background: #2d85bb;
      box-shadow: 0 4px 14px rgba(56, 154, 212, 0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: default;
      box-shadow: none;
    }
  }
}
</style>
